import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { ClassificationBadge } from "@/components/public/ClassificationBadge";
import { DetailModal, DetailModalSection } from "@/components/public/DetailModal";

/**
 * MaterialDetailModal — thin adapter around the shared DetailModal.
 * Fetches the material by slug and maps its fields to the modal slots.
 */
export function MaterialDetailModal({ slug, onClose }) {
  const enabled = Boolean(slug);
  const { data: m, isLoading } = useQuery({
    queryKey: ["material-detail", slug],
    queryFn: async () => (await api.get(`/public/materials/${slug}`)).data,
    enabled,
    retry: false,
  });

  if (!enabled) return null;

  const subtitle = m?.aliases?.length ? (
    <>
      <span className="uppercase tracking-wider mr-1">Also known as</span>
      {m.aliases.map((a, i) => (
        <span key={a} className="italic">{a}{i < m.aliases.length - 1 ? ", " : ""}</span>
      ))}
    </>
  ) : null;

  return (
    <DetailModal
      open={enabled}
      onClose={onClose}
      testId="material-detail-modal"
      title={isLoading || !m ? "Loading…" : m.name}
      subtitle={isLoading ? null : subtitle}
      badge={isLoading || !m ? null : <ClassificationBadge classification={m.classification} />}
      fullPageHref={m ? `/materials/${m.slug}` : undefined}
    >
      {isLoading || !m ? (
        <p className="text-sm text-muted_text">Loading…</p>
      ) : (
        <>
          {m.full_description && (
            <p className="text-sm text-muted_text leading-relaxed mb-5">{m.full_description}</p>
          )}
          <div className="space-y-4">
            <DetailModalSection title={`Why ${m.classification}`}>{m.classification_reason}</DetailModalSection>
            <DetailModalSection title="Sustainability notes">{m.sustainability_notes}</DetailModalSection>
            <DetailModalSection title="Biodegradability">{m.biodegradability_notes}</DetailModalSection>
            <DetailModalSection title="Common uses in clothing">{m.common_uses}</DetailModalSection>
            <DetailModalSection title="Relevant certifications">{m.certifications}</DetailModalSection>
            <DetailModalSection title="Better alternatives">{m.better_alternatives}</DetailModalSection>
            <DetailModalSection title="Source notes">{m.source_notes}</DetailModalSection>
          </div>
        </>
      )}
    </DetailModal>
  );
}
